import type { Slug } from "./BodyHighlighter";

// Colori per ogni distretto muscolare
// 4 colori base: rosso, arancione, giallo, blu
// Upper body e lower body usano la stessa palette
export const MUSCLE_COLORS: Record<string, string> = {
  // Upper body — frontale
  "SPALLE MID": "#EAB308",          // giallo
  "SPALLE ANTERIORI": "#F97316",    // arancione
  SPALLE: "#EAB308",                // giallo (legacy)
  "PETTORALI UPPER": "#EF4444",     // rosso
  "PETTORALI LOWER": "#D946EF",     // fucsia
  PETTORALI: "#EF4444",             // rosso (legacy)
  BICIPITI: "#EF4444",              // rosso
  AVAMBRACCI: "#F97316",            // arancione
  ADDOMINALI: "#2563EB",            // blu
  ADDUTTORI: "#D946EF",             // fucsia
  QUADRICIPITI: "#EAB308",          // giallo
  POLPACCI: "#2563EB",              // blu
  // Upper body — posteriore
  "HYBRID UPPER BACK": "#D946EF",   // fucsia
  TRAPEZIO: "#D946EF",              // fucsia (legacy)
  "SPALLE POSTERIORI": "#F97316",   // arancione
  TRICIPITI: "#EAB308",             // giallo
  "LOWER BACK": "#2563EB",          // blu
  DORSALI: "#2563EB",               // blu (legacy)
  // Lower body
  "GLUTEI SIDE": "#EAB308",         // giallo
  "GLUTEI MAX": "#EF4444",          // rosso
  GLUTEI: "#EF4444",                // rosso (legacy)
  FEMORALI: "#F97316",              // arancione
};

// Colori unici per il grafico — ogni distretto un colore diverso
export const MUSCLE_COLORS_UNIQUE: Record<string, string> = {
  PETTORALI: "#EF4444",           // rosso
  "PETTORALI UPPER": "#06B6D4",   // ciano
  "PETTORALI LOWER": "#D946EF",   // fucsia
  SPALLE: "#F59E0B",              // ambra
  "SPALLE ANTERIORI": "#F59E0B",  // ambra
  "SPALLE POSTERIORI": "#6366F1", // indaco
  "SPALLE MID": "#22C55E",        // verde
  BICIPITI: "#3B82F6",            // blu
  TRICIPITI: "#F97316",           // arancione
  ADDOMINALI: "#EF4444",          // rosso
  DORSALI: "#1D4ED8",             // blu scuro (legacy)
  "LOWER BACK": "#1D4ED8",       // blu scuro
  TRAPEZIO: "#A855F7",            // viola (legacy)
  "HYBRID UPPER BACK": "#A855F7", // viola
  AVAMBRACCI: "#D946EF",          // fucsia
  GLUTEI: "#EC4899",              // rosa
  "GLUTEI MAX": "#EC4899",        // rosa
  "GLUTEI SIDE": "#14B8A6",       // teal
  QUADRICIPITI: "#EAB308",        // giallo
  FEMORALI: "#FB923C",            // arancione chiaro
  POLPACCI: "#8B5CF6",            // viola chiaro
  ADDUTTORI: "#14B8A6",           // teal
};

export const getMuscleColorUnique = (district: string): string => {
  return MUSCLE_COLORS_UNIQUE[district] || "#9CA3AF";
};

// Mapping distretti italiani → slug del componente body-highlighter
// Ogni distretto può mappare a più slug (es. dorsali = upper-back + lower-back)
export const DISTRICT_TO_SLUGS: Record<string, Slug[]> = {
  GLUTEI: ["gluteal-max", "gluteal-side"],
  "GLUTEI MAX": ["gluteal-max"],
  "GLUTEI SIDE": ["gluteal-side"],
  FEMORALI: ["hamstring"],
  QUADRICIPITI: ["quadriceps"],
  POLPACCI: ["calves"],
  DORSALI: ["lower-back"],
  "LOWER BACK": ["lower-back"],
  TRAPEZIO: ["trapezius", "upper-back", "neck-back"],
  "HYBRID UPPER BACK": ["trapezius", "upper-back", "neck-back"],
  AVAMBRACCI: ["forearm"],
  ADDUTTORI: ["adductors"],
  "SPALLE ANTERIORI": ["deltoids-ant"],
  "SPALLE POSTERIORI": ["deltoids-post"],
  "SPALLE MID": ["deltoids-mid"],
  PETTORALI: ["chest-upper", "chest-lower"],
  "PETTORALI UPPER": ["chest-upper"],
  "PETTORALI LOWER": ["chest-lower"],
  SPALLE: ["deltoids"],
  TRICIPITI: ["triceps"],
  BICIPITI: ["biceps"],
  ADDOMINALI: ["abs", "obliques"],
};

// Mapping inverso: slug → distretto (per click/hover sul corpo)
export const SLUG_TO_DISTRICT: Record<string, string> = {};
for (const [district, slugs] of Object.entries(DISTRICT_TO_SLUGS)) {
  for (const slug of slugs) {
    SLUG_TO_DISTRICT[slug] = district;
  }
}

// Ordine fisso per la lista — nessun colore uguale adiacente
// Ordine dalla testa ai piedi, distretti simili raggruppati, colori alternati
// G=giallo A=arancione R=rosso F=fucsia B=blu
export const DISTRICT_DISPLAY_ORDER = [
  // Spalle: A G A (ant-arancione, mid-giallo, post-arancione)
  "SPALLE",              // G (legacy)
  "SPALLE ANTERIORI",    // A
  "SPALLE MID",          // G
  "SPALLE POSTERIORI",   // A
  // Petto: R F (upper-rosso, lower-fucsia) — upper prima per non avere F+F con hybrid
  "PETTORALI",           // R (legacy)
  "PETTORALI UPPER",     // R
  "PETTORALI LOWER",     // F
  // Schiena: B F (lower-back blu prima, poi hybrid fucsia — evita F+F col petto lower)
  "LOWER BACK",          // B
  "DORSALI",             // B (legacy)
  "HYBRID UPPER BACK",   // F
  "TRAPEZIO",            // F (legacy)
  // Braccia: R G A (bicipiti-rosso, tricipiti-giallo, avambracci-arancione)
  "BICIPITI",            // R
  "TRICIPITI",           // G
  "AVAMBRACCI",          // A
  // Addome: B
  "ADDOMINALI",          // B
  // Glutei: R G (max-rosso, side-giallo)
  "GLUTEI",              // R (legacy)
  "GLUTEI MAX",          // R
  "GLUTEI SIDE",         // G
  // Gambe: A G F B (femorali tra glutei side e quadricipiti per alternare)
  "FEMORALI",            // A
  "QUADRICIPITI",        // G
  "ADDUTTORI",           // F
  "POLPACCI",            // B
];

// Mapping TAG esercizio → distretto nella tabella
// I tag sono quelli usati dal PT sugli esercizi
// Ogni tag viene mappato al distretto corrispondente nella tabella
export const TAG_TO_DISTRICT: Record<string, string> = {
  // Tag esistenti (prima parte prima del trattino — retrocompatibilità)
  "PETTORALI": "PETTORALI",
  "PETTORALI-UPPER": "PETTORALI UPPER",
  "PETTORALI-LOWER": "PETTORALI LOWER",
  "SPALLE": "SPALLE",
  "BICIPITI": "BICIPITI",
  "TRICIPITI": "TRICIPITI",
  "ADDOMINALI": "ADDOMINALI",
  "DORSALI": "DORSALI",
  "GLUTEI": "GLUTEI",
  "QUADRICIPITI": "QUADRICIPITI",
  "FEMORALI": "FEMORALI",
  "POLPACCI": "POLPACCI",
  // Nuovi tag composti
  "LOWER-BACK": "LOWER BACK",
  "HYBRID-UPPER-BACK": "HYBRID UPPER BACK",
  "AVAMBRACCI": "AVAMBRACCI",
  "ADDUTTORI": "ADDUTTORI",
  "SPALLE-ANT": "SPALLE ANTERIORI",
  "SPALLE-POST": "SPALLE POSTERIORI",
  "SPALLE-MID": "SPALLE MID",
  "GLUTEI-MAX": "GLUTEI MAX",
  "GLUTEI-SIDE": "GLUTEI SIDE",
};

/**
 * Risolve un tag esercizio al distretto corrispondente.
 * Prima cerca il tag completo nella mappa, poi prova la prima parte prima del trattino.
 */
export const resolveTagToDistrict = (tag: string): string | null => {
  // Match esatto (es. "LOWER-BACK", "SPALLE-ANT")
  if (TAG_TO_DISTRICT[tag]) return TAG_TO_DISTRICT[tag];
  // Fallback: prima parte prima del trattino (es. "PETTORALI-UPPER" → "PETTORALI")
  const prefix = tag.split("-")[0];
  if (TAG_TO_DISTRICT[prefix]) return TAG_TO_DISTRICT[prefix];
  return prefix; // restituisce il prefix grezzo se non mappato
};

// Distretti selezionabili nel form muscleDistribution
// Solo i distretti "foglia" — quelli che il PT può assegnare agli esercizi
export const SELECTABLE_DISTRICTS = [
  "PETTORALI UPPER",
  "PETTORALI LOWER",
  "SPALLE ANTERIORI",
  "SPALLE MID",
  "SPALLE POSTERIORI",
  "HYBRID UPPER BACK",
  "LOWER BACK",
  "BICIPITI",
  "TRICIPITI",
  "AVAMBRACCI",
  "ADDOMINALI",
  "GLUTEI MAX",
  "GLUTEI SIDE",
  "QUADRICIPITI",
  "FEMORALI",
  "ADDUTTORI",
  "POLPACCI",
];

// Distretti generali per esercizi CHECK (macro gruppi muscolari)
export const CHECK_DISTRICTS = [
  "PETTORALI",
  "SPALLE",
  "DORSALI",
  "BICIPITI",
  "TRICIPITI",
  "ADDOMINALI",
  "GLUTEI",
  "QUADRICIPITI",
  "FEMORALI",
  "POLPACCI",
];

// Normalizza varianti specifiche → distretto generico CHECK
// Serve per mappare i click SVG (che restituiscono varianti specifiche)
// al distretto generico usato nella sezione danno/recupero
export const CHECK_DISTRICT_NORMALIZE: Record<string, string> = {
  "GLUTEI MAX": "GLUTEI",
  "GLUTEI SIDE": "GLUTEI",
  "PETTORALI UPPER": "PETTORALI",
  "PETTORALI LOWER": "PETTORALI",
  "SPALLE ANTERIORI": "SPALLE",
  "SPALLE POSTERIORI": "SPALLE",
  "SPALLE MID": "SPALLE",
  "LOWER BACK": "DORSALI",
  "HYBRID UPPER BACK": "DORSALI",
  TRAPEZIO: "DORSALI",
};

// Distretti da escludere dalla tabella riepilogo
export const EXCLUDED_DISTRICTS = [
  "CARDIO",
  "CORE",
  "CIFOSI",
  "PREHAB SCAPOLE",
  "PREHAB PIEDE",
  "PREHAB BACINO",
  "REHAB SPALLA",
  "REHAB COLLO",
  "REHAB LOMBARE",
  "REHAB GINOCCHIO",
];

export const getMuscleColor = (district: string): string => {
  return MUSCLE_COLORS[district] || "#9CA3AF"; // fallback grigio
};

// Colori per macro gruppo muscolare — usati per gradiente esercizi e body map volume
export const MACRO_GROUP_COLORS: Record<string, string> = {
  PETTO: "#3B82F6",         // Blu
  SPALLE: "#6366F1",        // Indaco
  DORSO: "#22C55E",         // Verde
  BICIPITI: "#A855F7",      // Viola
  TRICIPITI: "#38BDF8",     // Azzurro
  AVAMBRACCI: "#C084FC",    // Viola chiaro
  ADDOMINALI: "#D946EF",    // Fucsia
  GLUTEI: "#EAB308",        // Giallo
  QUADRICIPITI: "#EF4444",  // Rosso
  FEMORALI: "#F97316",      // Arancione leggero
  ADDUTTORI: "#F87171",     // Rosso chiaro
  POLPACCI: "#FB923C",      // Arancione acceso
};

// Mapping sotto-distretto → macro gruppo
export const DISTRICT_TO_MACRO_GROUP: Record<string, string> = {
  "PETTORALI UPPER": "PETTO",
  "PETTORALI LOWER": "PETTO",
  PETTORALI: "PETTO",
  "SPALLE ANTERIORI": "SPALLE",
  "SPALLE MID": "SPALLE",
  "SPALLE POSTERIORI": "SPALLE",
  SPALLE: "SPALLE",
  "HYBRID UPPER BACK": "DORSO",
  "LOWER BACK": "DORSO",
  DORSALI: "DORSO",
  TRAPEZIO: "DORSO",
  BICIPITI: "BICIPITI",
  TRICIPITI: "TRICIPITI",
  AVAMBRACCI: "AVAMBRACCI",
  ADDOMINALI: "ADDOMINALI",
  "GLUTEI MAX": "GLUTEI",
  "GLUTEI SIDE": "GLUTEI",
  GLUTEI: "GLUTEI",
  FEMORALI: "FEMORALI",
  QUADRICIPITI: "QUADRICIPITI",
  ADDUTTORI: "ADDUTTORI",
  POLPACCI: "POLPACCI",
};

// Palette colori per la sezione Volume Muscolare — sfumature per sotto-distretti
export const MUSCLE_COLORS_VOLUME: Record<string, string> = {
  // Petto — Blu
  "PETTORALI UPPER": "#3B82F6",
  "PETTORALI LOWER": "#2563EB",
  PETTORALI: "#3B82F6",
  // Spalle — Indaco
  "SPALLE ANTERIORI": "#6366F1",
  "SPALLE MID": "#818CF8",
  "SPALLE POSTERIORI": "#4F46E5",
  SPALLE: "#6366F1",
  // Dorso — Verde
  "HYBRID UPPER BACK": "#22C55E",
  "LOWER BACK": "#16A34A",
  DORSALI: "#22C55E",
  TRAPEZIO: "#15803D",
  // Bicipiti — Viola
  BICIPITI: "#A855F7",
  // Tricipiti — Azzurro
  TRICIPITI: "#38BDF8",
  // Avambracci — Viola chiaro
  AVAMBRACCI: "#C084FC",
  // Addominali — Fucsia
  ADDOMINALI: "#D946EF",
  // Glutei — Giallo
  "GLUTEI MAX": "#EAB308",
  "GLUTEI SIDE": "#CA8A04",
  GLUTEI: "#EAB308",
  // Quadricipiti — Rosso
  QUADRICIPITI: "#EF4444",
  // Femorali — Arancione leggero
  FEMORALI: "#F97316",
  // Adduttori — Rosso chiaro
  ADDUTTORI: "#F87171",
  // Polpacci — Arancione acceso
  POLPACCI: "#FB923C",
};

export const getMuscleColorVolume = (district: string): string => {
  return MUSCLE_COLORS_VOLUME[district] || MACRO_GROUP_COLORS[DISTRICT_TO_MACRO_GROUP[district]] || "#9CA3AF";
};

// Gerarchia muscolare per il dropdown del tonnellaggio
// Ogni gruppo ha un label e i distretti foglia associati
export const DISTRICT_HIERARCHY: { label: string; children: string[] }[] = [
  { label: "PETTO", children: ["PETTORALI UPPER", "PETTORALI LOWER"] },
  { label: "SPALLE", children: ["SPALLE ANTERIORI", "SPALLE MID", "SPALLE POSTERIORI"] },
  { label: "SCHIENA", children: ["HYBRID UPPER BACK", "LOWER BACK"] },
  { label: "BRACCIA", children: ["BICIPITI", "TRICIPITI", "AVAMBRACCI"] },
  { label: "GLUTEI", children: ["GLUTEI MAX", "GLUTEI SIDE"] },
  { label: "GAMBE", children: ["QUADRICIPITI", "FEMORALI", "ADDUTTORI", "POLPACCI"] },
  { label: "ADDOMINALI", children: ["ADDOMINALI"] },
];
